// POPULATION panel — two sections, Lineage (top) and Education (bottom).
// Lineage: spend wealth + 2 goons → 1 high-tier goon (the "Royal Pairing"
// breeding mechanic). Distinct from player-to-player Marriage, which lives
// in the Registry. The MARRIAGE license still gates lineage because a
// licensed officiant is required to perform the pairing ceremony.
// Education: per-goon level up that multiplies that goon's click rate.

import { setHtmlIfChanged } from './domUtil.js'
import { appendBuyCoinsLink } from './buyCoinsLink.js'
import { Events, on, emit } from './eventBus.js'
import { GoonConfig, GoonTypes, GoonTrainingConfig as EducationConfig, CitizenRoles, CitizenRoleOrder } from '../config/balance.js'
import {
  marriageRecipes,
  canMarry,
  educationCost,
  canEducate,
} from '../game/populationManager.js'
import { hasLicense } from '../game/profileManager.js'
import { LicenseTypes } from '../config/balance.js'
import { getGoonsOwned, getEducationLevel } from '../game/seasonManager.js'
import { getCitizens, promoteCitizen, promotedCount, typeLabel } from '../game/citizens.js'
import { isResting, restRemainingMs, startRest, stopRest } from '../game/restManager.js'
import { RestConfig } from '../config/balance.js'
import { makeModalToggleable } from './railPanel.js'
import { formatCoins } from './format.js'

export function mountPopulationPanel({ root, toggleBtn }) {
  root.innerHTML = `
    <div class="hud__build-header">
      <span class="hud__build-title">CITIZENS</span>
      <span class="hud__build-tiles" data-citizen-summary>—</span>
    </div>
    <div class="hud__citizen-actions">
      <button class="hud__build-buy" data-open-feed><span class="lbl">🍚 FEED</span></button>
      <button class="hud__build-buy" data-open-tax><span class="lbl">⚖ TAX</span></button>
      <button class="hud__build-buy" data-rest><span class="lbl" data-rest-lbl>🌙 REST</span><span class="price" data-rest-sub hidden></span></button>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">CITIZEN REGISTRY</div>
      <div class="hud__pop-hint">Promote a citizen to a trade — civic honor, no coin. Schooled cats wear a 🎓.</div>
      <div class="hud__citizen-list" data-citizens></div>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">LINEAGE</div>
      <div data-marriages>${marriageRecipes().map(marriageRow).join('')}</div>
    </div>
    <div class="hud__pop-section">
      <div class="hud__pop-section-title">EDUCATION</div>
      <div data-education>${Object.values(GoonTypes).map(educationRow).join('')}</div>
    </div>
  `

  // FEED + TAX both act directly on the citizens, so they live here now (off the
  // left rail). The buttons open their existing panels.
  const openRail = (sel) => {
    const target = document.querySelector(sel)
    if (!target) return
    document.querySelectorAll('[data-rail-panel]').forEach((p) => p.classList.remove('open'))
    target.classList.add('open')
  }
  root.querySelector('[data-open-feed]')?.addEventListener('click', () => openRail('[data-feed-panel]'))
  root.querySelector('[data-open-tax]')?.addEventListener('click', () => openRail('[data-tax-panel]'))

  // REST ⇄ WORK. REST sends every citizen to the nearest building to sleep; the same green button then reads WORK (with the time
  // left) and sends them back out. Left alone, they return to work by themselves (RestConfig.durationMs).
  const restBtn = root.querySelector('[data-rest]')
  const restLbl = root.querySelector('[data-rest-lbl]')
  const restSub = root.querySelector('[data-rest-sub]')
  const refreshRest = () => {
    const resting = isResting()
    const ms = restRemainingMs()
    restLbl.textContent = resting ? '⚒ WORK' : '🌙 REST'
    restSub.hidden = !resting
    restSub.textContent = resting ? `back in ${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}` : ''
    restBtn.title = resting
      ? 'Send your citizens back out to work now'
      : `Send your citizens to rest in the nearest building. They earn nothing while they rest, and go back to work by themselves after ${Math.round(RestConfig.durationMs / 60000)} minutes.`
  }
  restBtn.addEventListener('click', () => { if (isResting()) stopRest(false); else startRest(); refreshRest() })
  on(Events.RestChange, refreshRest)
  on(Events.KingdomChange, refreshRest)
  on(Events.AccountStatePulled, refreshRest)
  setInterval(refreshRest, 1000)
  refreshRest()

  for (const [idx, recipe] of marriageRecipes().entries()) {
    root.querySelector(`[data-marriage="${idx}"]`).addEventListener('click', () => {
      emit(Events.MarriageRequested, { recipe })
    })
  }
  for (const id of Object.values(GoonTypes)) {
    root.querySelector(`[data-edu-buy="${id}"]`)?.addEventListener('click', () => {
      emit(Events.EducationRequested, { goonId: id })
    })
  }

  const citizensHost = root.querySelector('[data-citizens]')
  const summaryEl = root.querySelector('[data-citizen-summary]')

  const renderCitizens = () => {
    const list = getCitizens().sort((a, b) =>
      a.type.localeCompare(b.type) || a.name.localeCompare(b.name),
    )
    const promoted = promotedCount()
    summaryEl.textContent = `${list.length} citizen${list.length === 1 ? '' : 's'} · ${promoted} promoted`
    summaryEl.classList.toggle('active', promoted > 0)
    const html = list.length
      ? list.map(citizenRow).join('')
      : `<div class="hud__build-row"><div class="hud__build-desc dim">No citizens yet — buy Peasants and Scribes below.</div></div>`
    if (!setHtmlIfChanged(citizensHost, html)) return // unchanged: leave open dropdowns / pending clicks alone
    citizensHost.querySelectorAll('[data-promote]').forEach((sel) => {
      sel.addEventListener('change', () => {
        promoteCitizen(sel.dataset.promote, sel.value || null)
      })
    })
  }

  const refresh = () => {
    renderCitizens()
    const licensed = hasLicense(LicenseTypes.MARRIAGE)
    for (const [idx, recipe] of marriageRecipes().entries()) {
      const btn = root.querySelector(`[data-marriage="${idx}"]`)
      if (!btn) continue
      btn.disabled = !canMarry(recipe)
      // `data-have` lives in the row's description, not the button — query
      // the row via closest() so we get the right element.
      const haveEl = btn.closest('.hud__build-row')?.querySelector(`[data-have="${idx}"]`)
      if (haveEl) haveEl.textContent = `${getGoonsOwned(recipe.from)}/${recipe.requires}`
      const lblEl = btn.querySelector('.lbl')
      if (lblEl) lblEl.textContent = licensed ? 'PAIR' : 'NEED LICENSE'
    }
    for (const id of Object.values(GoonTypes)) {
      const row = root.querySelector(`[data-edu-row="${id}"]`)
      if (!row) continue
      const level = getEducationLevel(id)
      row.querySelector('[data-level]').textContent =
        `LVL ${level} (+${Math.round(level * EducationConfig.levelBonus * 100)}%)`
      const cost = educationCost(id)
      const btn = row.querySelector('[data-edu-buy]')
      if (level >= EducationConfig.maxLevel) {
        btn.disabled = true
        btn.querySelector('.price').textContent = 'MAX'
      } else {
        btn.disabled = !canEducate(id)
        btn.querySelector('.price').textContent = formatCoins(cost)
      }
    }
  }

  on(Events.WealthChange, refresh)
  on(Events.GoonOwnedChange, refresh)
  on(Events.EducationLevelChange, refresh)
  on(Events.CitizenPromoted, refresh)
  on(Events.CitizenEducated, refresh)
  on(Events.CitizenInjured, refresh)
  on(Events.CitizenHealed, refresh)
  on(Events.KingdomChange, refresh)
  on(Events.LicensePurchased, refresh)
  on(Events.ProfileChange, refresh)
  refresh()
  appendBuyCoinsLink(root) // quiet link to the Shop, at the foot of the pop-up
  makeModalToggleable(root, toggleBtn)
}

function citizenRow(c) {
  const opts = ['<option value="">Unassigned</option>']
    .concat(CitizenRoleOrder.map((r) =>
      `<option value="${r}" ${c.role === r ? 'selected' : ''}>${CitizenRoles[r].icon} ${CitizenRoles[r].label}</option>`,
    ))
    .join('')
  return `
    <div class="hud__citizen-row">
      <div class="hud__citizen-id">
        <span class="hud__citizen-name">${escapeHtml(c.name)}${c.educated ? ' 🎓' : ''}</span>
        <span class="hud__citizen-type">${escapeHtml(typeLabel(c.type))}</span>
      </div>
      <select class="hud__citizen-role" data-promote="${c.id}">${opts}</select>
    </div>
  `
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ))
}

function marriageRow(recipe, idx) {
  const fromCfg = GoonConfig[recipe.from]
  const toCfg = GoonConfig[recipe.produces]
  return `
    <div class="hud__build-row">
      <div class="hud__build-info">
        <div class="hud__build-name">${recipe.label}</div>
        <div class="hud__build-desc">→ 1 ${toCfg.name} (${fromCfg.name} <span data-have="${idx}">0/${recipe.requires}</span>)</div>
      </div>
      <button class="hud__build-buy" data-marriage="${idx}">
        <span class="lbl">PAIR</span>
        <span class="price">${formatCoins(recipe.cost)}</span>
      </button>
    </div>
  `
}

function educationRow(id) {
  const cfg = GoonConfig[id]
  return `
    <div class="hud__build-row" data-edu-row="${id}">
      <div class="hud__build-info">
        <div class="hud__build-name">${cfg.name}</div>
        <div class="hud__build-desc" data-level>LVL 0</div>
      </div>
      <button class="hud__build-buy" data-edu-buy="${id}">
        <span class="lbl">TRAIN</span>
        <span class="price">0m</span>
      </button>
    </div>
  `
}

